// The Leads page: ask Claude (in the Claude app, with the Gold connector) to find
// stocks of interest; they come back through the connector and are listed here to work through.
import { api } from "./api";
import { starsHtml } from "./assess";
import { askClaudeUrl } from "./connector";
import { codePill, compact, date, dateTime, esc, linkCodes, pct, price, stockAttr, stockLink } from "./format";
import { markdown } from "./markdown";

const STRATEGIES: [string, string][] = [
  ["buzz_catalysts", "Buzz and catalysts"],
  ["quality_discount", "Quality at a discount"],
  ["insider_buying", "Insider buying"],
  ["themes_winners", "Themes and like my winners"],
];
const STATUS_TABS = [["new", "New"], ["reviewing", "Reviewing"], ["interested", "Interested"], ["dismissed", "Dismissed"], ["all", "All"]] as const;

interface Lead {
  id: number; code: string; name: string | null; status: string; why: string; strategies: string[];
  sources: { url: string; title?: string; date?: string }[];
  quick: { price: number | null; marketCap: number | null; dailyValue: number | null; return3m: number | null; fromHigh: number | null } | null;
  provisionalStars: number | null; timesFound: number; firstFoundAt: string; lastFoundAt: string; notes: string | null; dismissedReason: string | null;
  priceFirst?: number | null; priceNow?: number | null; sinceFound?: number | null; newsletters?: string[];
}

// The "All leads" table: every lead, sortable by any column, filterable by status.
type SortKey = "code" | "age" | "last" | "stars" | "assessed" | "since" | "times" | "cap" | "daily" | "r3m" | "high" | "status";
const COLUMNS: [SortKey, string, string][] = [
  ["code", "Stock", "Code and name"], ["status", "Status", "Your decision"], ["age", "Found", "When first found (age)"],
  ["last", "Last found", "When last found"], ["times", "×", "Times found"], ["stars", "Gold ★", "Gold's quick stars when last found"],
  ["assessed", "Assessed ★", "Investment Health from the latest assessment"], ["since", "Since found", "Price now against when first found"],
  ["cap", "Mkt cap", "Market cap"], ["daily", "$/day", "Average traded a day"], ["r3m", "3 mo", "Return over 3 months when found"], ["high", "From high", "Below its 52-week high when found"],
];
let sort: { key: SortKey; dir: 1 | -1 } = (() => {
  try { return JSON.parse(localStorage.getItem("gold.leadSort") ?? "null") ?? { key: "age", dir: 1 }; } catch { return { key: "age", dir: 1 }; }
})();
let tableStatus = "active";

function sortValue(l: Lead, key: SortKey, ratings: Record<string, { health: number }>): number | string | null {
  switch (key) {
    case "code": return l.code;
    case "status": return ["interested", "reviewing", "new", "dismissed"].indexOf(l.status);
    case "age": return Date.now() - Date.parse(l.firstFoundAt); // how old: ascending = newest first
    case "last": return Date.parse(l.lastFoundAt);
    case "times": return l.timesFound;
    case "stars": return l.provisionalStars;
    case "assessed": return ratings[l.code]?.health ?? null;
    case "since": return l.sinceFound ?? null;
    case "cap": return l.quick?.marketCap ?? null;
    case "daily": return l.quick?.dailyValue ?? null;
    case "r3m": return l.quick?.return3m ?? null;
    case "high": return l.quick?.fromHigh ?? null;
  }
}

function leadTable(leads: Lead[], ratings: Record<string, { health: number }>) {
  const now = Date.now();
  const days = (iso: string) => Math.max(0, Math.round((now - Date.parse(iso)) / 864e5));
  const shown = leads.filter((l) => tableStatus === "all" || (tableStatus === "active" ? l.status !== "dismissed" : l.status === tableStatus));
  const rows = [...shown].sort((a, b) => {
    const x = sortValue(a, sort.key, ratings), y = sortValue(b, sort.key, ratings);
    if (x == null && y == null) return 0;
    if (x == null) return 1; // blanks last either way
    if (y == null) return -1;
    const cmp = typeof x === "string" ? x.localeCompare(String(y)) : (x as number) - (y as number);
    return cmp * sort.dir;
  });
  const arrow = (k: SortKey) => (sort.key === k ? (sort.dir === -1 ? " ▼" : " ▲") : "");
  return `<div class="lead-table-tools"><label class="small">Show <select id="lt-status">${[["active", "All but dismissed"], ["all", "Everything"], ["new", "New"], ["reviewing", "Reviewing"], ["interested", "Interested"], ["dismissed", "Dismissed"]].map(([k, l]) => `<option value="${k}"${tableStatus === k ? " selected" : ""}>${l}</option>`).join("")}</select></label>
    <span class="muted small">${rows.length} lead${rows.length === 1 ? "" : "s"} · tap a heading to sort</span></div>
    <div class="table-wrap"><table class="lead-table"><thead><tr>${COLUMNS.map(([k, l, title]) => `<th><button class="th-sort${sort.key === k ? " on" : ""}" data-sort="${k}" title="${esc(title)}">${l}${arrow(k)}</button></th>`).join("")}</tr></thead>
    <tbody>${rows.map((l) => `<tr>
      <th>${stockLink(l.code)} <small class="muted">${esc((l.name ?? "").slice(0, 28))}</small></th>
      <td><span class="pill">${esc(l.status)}</span></td>
      <td title="${esc(date(l.firstFoundAt))}">${days(l.firstFoundAt)}d</td>
      <td title="${esc(date(l.lastFoundAt))}">${days(l.lastFoundAt)}d</td>
      <td>${l.timesFound}</td>
      <td>${l.provisionalStars != null ? starsHtml(l.provisionalStars) : "–"}</td>
      <td>${ratings[l.code] ? `<a href="#/${esc(l.code)}/assess">${starsHtml(ratings[l.code].health)}</a>` : "–"}</td>
      <td class="${(l.sinceFound ?? 0) >= 0 ? "up" : "down"}">${l.sinceFound != null ? pct(l.sinceFound, true) : "–"}</td>
      <td>${l.quick?.marketCap != null ? "$" + compact(l.quick.marketCap) : "–"}</td>
      <td>${l.quick?.dailyValue != null ? "$" + compact(l.quick.dailyValue) : "–"}</td>
      <td class="${(l.quick?.return3m ?? 0) >= 0 ? "up" : "down"}">${pct(l.quick?.return3m ?? null, true)}</td>
      <td>${pct(l.quick?.fromHigh ?? null, true)}</td>
    </tr>`).join("") || `<tr><td colspan="${COLUMNS.length}" class="muted">No leads here.</td></tr>`}</tbody></table></div>
    <p class="muted small">Gold ★ are its quick stars from its own numbers; Assessed ★ are from Claude's latest assessment. Since found compares today's price with the price when it was first found. Tap a code to open the stock.</p>`;
}
interface Settings { minMarketCap: number; minDailyValue: number; maxPerRun: number; themes: string }
interface Pick {
  findId: number; leadId: number; code: string; name: string | null; status: string | null; strategy: string; why: string;
  signals: Record<string, string | number | undefined> | null; sources: { url: string; title?: string; date?: string }[];
  stars: number | null; quick: Lead["quick"]; foundAt: string; priceNow: number | null;
}
interface Run {
  id: number; ranAt: string; strategies: string[]; themes: string | null; hotcopperLoggedIn: string; summary: string | null; model: string | null;
  sources: { source: string; reached: boolean; note?: string }[]; added: number; skipped: { code: string; reason: string }[]; picks: Pick[];
}
interface LeadsResponse { leads: Lead[]; settings: Settings; runs: Run[]; unreadEmails?: number }

const label = (k: string) => STRATEGIES.find(([x]) => x === k)?.[1] ?? k;

export function leadPrompt(strategies: string[], themes: string) {
  return `Find new stock leads for Gold. Call get_lead_brief from the Gold connector with strategies ${JSON.stringify(strategies)}${themes ? ` and themes "${themes}"` : ""}, follow it (ASX announcements, news, Reddit and other web sources, and read every unread resource of mine it includes or lists, opening listed ones with read_resources), and send the leads to Gold with save_leads, listing the resources you read. Tell me what was added and what was skipped.`;
}

let tab: string = "new";
let view: "runs" | "list" | "table" = "runs";
const remembered = (): string[] => {
  try { return JSON.parse(localStorage.getItem("gold.leadStrategies") ?? "null") ?? STRATEGIES.map(([k]) => k); } catch { return STRATEGIES.map(([k]) => k); }
};

export async function renderLeads(app: HTMLElement) {
  app.innerHTML = `<section class="leads"><h2>Leads</h2><div id="leads-body"><div class="loading" role="status"><span></span>Loading…</div></div></section>`;
  const body = document.getElementById("leads-body")!;
  let data: LeadsResponse;
  let ratings: Record<string, { health: number }> = {};
  try {
    const [d, att] = await Promise.all([api<LeadsResponse>("leads"), api<{ ratings: typeof ratings }>("attention").catch(() => ({ ratings: {} }))]);
    data = d;
    ratings = att.ratings;
  } catch (e) {
    body.innerHTML = `<div class="notice error">${esc((e as Error).message)}</div>`;
    return;
  }
  const chosen = remembered();
  const s = data.settings;
  const counts = Object.fromEntries(STATUS_TABS.map(([k]) => [k, k === "all" ? data.leads.length : data.leads.filter((l) => l.status === k).length]));
  const shown = data.leads.filter((l) => tab === "all" || l.status === tab);

  body.innerHTML = `
    <section class="card generate">
      <h3>Find new leads</h3>
      <div class="checks">${STRATEGIES.map(([k, l]) => `<label><input type="checkbox" name="strategy" value="${k}"${chosen.includes(k) ? " checked" : ""}> ${l}</label>`).join("")}</div>
      <label class="themes">Themes (for "Themes and like my winners")<input name="themes" value="${esc(s.themes)}" placeholder="e.g. uranium, defence, healthcare"></label>
      <div class="gen-actions">
        <button class="primary" id="copy-prompt">Ask Claude to generate leads</button>
        <a class="ghost" id="open-web" target="_blank" rel="noopener">Open in claude.ai</a>
      </div>
      <p class="muted small" id="gen-help">The button copies the request: paste it into a chat in your Claude app with the Gold connector switched on. "Open in claude.ai" starts the chat for you.</p>
      ${data.unreadEmails ? `<p class="small"><a href="#/resources">${data.unreadEmails} unread resource${data.unreadEmails > 1 ? "s" : ""}</a> (emails and uploads) will go to Claude with this request.</p>` : ""}
      <details><summary class="small">Filters</summary>
        <form id="lead-settings" class="settings">
          <label>Smallest market cap ($m)<input name="minMarketCap" type="number" min="0" step="1" value="${s.minMarketCap / 1e6}"></label>
          <label>Smallest $ traded a day ($k)<input name="minDailyValue" type="number" min="0" step="10" value="${s.minDailyValue / 1e3}"></label>
          <label>Most leads per run<input name="maxPerRun" type="number" min="1" max="20" value="${s.maxPerRun}"></label>
          <button class="ghost small" type="submit">Save filters</button>
        </form>
        <p class="muted small">Held, watched and dismissed stocks are always left out unless something important changed.</p>
      </details>
    </section>
    <nav class="seg lead-view">
      <button data-view="runs" class="${view === "runs" ? "on" : ""}">Previous lead runs <small>${data.runs.length}</small></button>
      <button data-view="list" class="${view === "list" ? "on" : ""}">Working list <small>${data.leads.length}</small></button>
      <button data-view="table" class="${view === "table" ? "on" : ""}">All leads table</button>
    </nav>
    ${view === "table" ? leadTable(data.leads, ratings) : view === "runs"
      ? `<div class="runs">${data.runs.map((r, i) => runCard(r, ratings, i === 0)).join("") || `<p class="muted">No lead runs yet. Ask Claude to generate some.</p>`}</div>`
      : `<nav class="seg lead-tabs">${STATUS_TABS.map(([k, l]) => `<button data-tab="${k}" class="${tab === k ? "on" : ""}">${l} <small>${counts[k]}</small></button>`).join("")}</nav>
         <ul class="lead-list">${shown.map(card).join("") || `<li class="muted">${data.leads.length ? "Nothing here." : "No leads yet. Ask Claude to generate some."}</li>`}</ul>`}`;

  const strategiesNow = () => [...body.querySelectorAll<HTMLInputElement>("input[name=strategy]:checked")].map((i) => i.value);
  const themesNow = () => (body.querySelector<HTMLInputElement>("input[name=themes]")!.value || "").trim();
  const syncWeb = () => {
    body.querySelector<HTMLAnchorElement>("#open-web")!.href = `https://claude.ai/new?q=${encodeURIComponent(leadPrompt(strategiesNow(), themesNow()))}`;
    try { localStorage.setItem("gold.leadStrategies", JSON.stringify(strategiesNow())); } catch { /* private mode */ }
  };
  syncWeb();
  body.querySelectorAll("input[name=strategy], input[name=themes]").forEach((i) => i.addEventListener("input", syncWeb));
  body.querySelector("#copy-prompt")!.addEventListener("click", async (e) => {
    const b = e.currentTarget as HTMLButtonElement;
    if (!strategiesNow().length) { b.textContent = "Pick at least one strategy"; return; }
    const themes = themesNow();
    if (themes !== s.themes) api("leads/settings", "PUT", { themes }).catch(() => {});
    try {
      await navigator.clipboard.writeText(leadPrompt(strategiesNow(), themes));
      b.textContent = "Copied: paste it into the Claude desktop app";
    } catch {
      prompt("Copy this into the Claude desktop app:", leadPrompt(strategiesNow(), themes));
    }
  });
  body.querySelector("#lead-settings")!.addEventListener("submit", async (e) => {
    e.preventDefault();
    const f = new FormData(e.target as HTMLFormElement);
    await api("leads/settings", "PUT", { minMarketCap: Number(f.get("minMarketCap")) * 1e6, minDailyValue: Number(f.get("minDailyValue")) * 1e3, maxPerRun: Number(f.get("maxPerRun")) });
    renderLeads(app);
  });
  body.querySelectorAll<HTMLButtonElement>(".lead-tabs button").forEach((b) => b.addEventListener("click", () => { tab = b.dataset.tab!; renderLeads(app); }));
  body.querySelectorAll<HTMLButtonElement>(".lead-view button").forEach((b) => b.addEventListener("click", () => { view = b.dataset.view as typeof view; renderLeads(app); }));
  body.querySelectorAll<HTMLButtonElement>("[data-sort]").forEach((b) => b.addEventListener("click", () => {
    const key = b.dataset.sort as SortKey;
    sort = sort.key === key ? { key, dir: sort.dir === 1 ? -1 : 1 } : { key, dir: key === "code" || key === "age" || key === "last" ? 1 : -1 };
    try { localStorage.setItem("gold.leadSort", JSON.stringify(sort)); } catch { /* private mode */ }
    renderLeads(app);
  }));
  body.querySelector<HTMLSelectElement>("#lt-status")?.addEventListener("change", (e) => { tableStatus = (e.target as HTMLSelectElement).value; renderLeads(app); });
  // Actions on picks inside a run report.
  body.querySelectorAll<HTMLElement>(".pick-row").forEach((el) => {
    const id = Number(el.dataset.lead);
    el.querySelector("[data-act=watch]")?.addEventListener("click", async () => { await api(`leads/${id}/watch`, "POST"); renderLeads(app); });
    el.querySelector<HTMLSelectElement>("select")?.addEventListener("change", async (e) => {
      const status = (e.target as HTMLSelectElement).value;
      const reason = status === "dismissed" ? prompt("Why dismiss it? (remembered for future lead runs)", "") : null;
      if (status === "dismissed" && reason === null) return renderLeads(app);
      await api(`leads/${id}`, "PATCH", { status, ...(reason != null ? { dismissed_reason: reason } : {}) });
      renderLeads(app);
    });
  });

  body.querySelectorAll<HTMLElement>(".lead").forEach((el) => {
    const id = Number(el.dataset.id);
    const lead = data.leads.find((l) => l.id === id)!;
    el.querySelector("[data-act=watch]")?.addEventListener("click", async () => { await api(`leads/${id}/watch`, "POST"); renderLeads(app); });
    el.querySelector("[data-act=dismiss]")?.addEventListener("click", async () => {
      const reason = prompt(`Why dismiss ${lead.code}? (remembered for future lead runs)`, "");
      if (reason === null) return;
      await api(`leads/${id}`, "PATCH", { status: "dismissed", dismissed_reason: reason });
      renderLeads(app);
    });
    el.querySelector<HTMLSelectElement>("select")?.addEventListener("change", async (e) => {
      await api(`leads/${id}`, "PATCH", { status: (e.target as HTMLSelectElement).value });
      renderLeads(app);
    });
    const notes = el.querySelector<HTMLTextAreaElement>("textarea");
    notes?.addEventListener("change", () => api(`leads/${id}`, "PATCH", { notes: notes.value }).then(() => (notes.classList.add("saved"), setTimeout(() => notes.classList.remove("saved"), 1500))));
    el.querySelector<HTMLDetailsElement>("details.history")?.addEventListener("toggle", async (e) => {
      const d = e.target as HTMLDetailsElement;
      if (!d.open || d.dataset.loaded) return;
      d.dataset.loaded = "1";
      const { finds } = await api<{ finds: { foundAt: string; strategy: string; why: string; signals: Record<string, unknown> | null }[] }>(`leads/${id}/history`);
      d.querySelector("ol")!.innerHTML = finds.map((f) => `<li><b>${dateTime(f.foundAt)}</b> · ${esc(label(f.strategy))}<br>${esc(f.why)}${f.signals?.catalyst ? `<br><span class="muted small">Catalyst: ${esc(f.signals.catalyst)}${f.signals.catalyst_date ? ` (${esc(f.signals.catalyst_date)})` : ""}</span>` : ""}${f.signals?.insider_buying ? `<br><span class="muted small">Insider buying: ${esc(f.signals.insider_buying)}</span>` : ""}</li>`).join("");
    });
  });
}

function card(l: Lead): string {
  const q = l.quick;
  return `<li class="card lead" data-id="${l.id}">
    <div class="lead-head">
      ${stockLink(l.code)} <span class="muted">${esc(l.name ?? "")}</span>
      ${l.provisionalStars != null ? `<span title="Gold's quick check from its own numbers only, before any assessment">${starsHtml(l.provisionalStars)}</span>` : ""}
    </div>
    <div class="chips">${l.strategies.map((s) => `<span class="chip">${esc(label(s))}</span>`).join("")}<span class="muted small">found ${l.timesFound}× · last ${date(l.lastFoundAt)}</span></div>
    <p>${esc(l.why).replace(/\n/g, "<br>")}</p>
    ${q ? `<dl class="stats quick">
      <div class="stat"><dt>Price</dt><dd>${price(q.price)}</dd></div>
      <div class="stat"><dt>Market cap</dt><dd>${q.marketCap != null ? "$" + compact(q.marketCap) : "–"}</dd></div>
      <div class="stat"><dt>Traded a day</dt><dd>${q.dailyValue != null ? "$" + compact(q.dailyValue) : "–"}</dd></div>
      <div class="stat"><dt>3 months</dt><dd class="${(q.return3m ?? 0) >= 0 ? "up" : "down"}">${pct(q.return3m, true)}</dd></div>
      <div class="stat"><dt>From 52-week high</dt><dd>${pct(q.fromHigh, true)}</dd></div>
    </dl>` : ""}
    ${l.sources.length ? `<details><summary class="small">${l.sources.length} source${l.sources.length > 1 ? "s" : ""}</summary><ol class="small">${l.sources.map((s) => `<li><a href="${esc(s.url)}" target="_blank" rel="noopener">${esc(s.title || s.url)}</a>${s.date ? ` (${date(s.date)})` : ""}</li>`).join("")}</ol></details>` : ""}
    ${l.status === "dismissed" && l.dismissedReason ? `<p class="muted small">Dismissed: ${esc(l.dismissedReason)}</p>` : ""}
    <textarea rows="2" placeholder="Your notes" aria-label="Notes on ${esc(l.code)}">${esc(l.notes ?? "")}</textarea>
    <div class="lead-actions">
      <select aria-label="Status">${["new", "reviewing", "interested", "dismissed"].map((s) => `<option value="${s}"${l.status === s ? " selected" : ""}>${s[0].toUpperCase() + s.slice(1)}</option>`).join("")}</select>
      <button class="ghost small" data-act="watch">☆ Watch</button>
      <a class="ghost small" href="${esc(askClaudeUrl(l.code))}" target="_blank" rel="noopener">Assess</a>
      ${l.status !== "dismissed" ? `<button class="ghost small" data-act="dismiss">Dismiss</button>` : ""}
    </div>
    <details class="history"><summary class="small">History</summary><ol class="small"></ol></details>
  </li>`;
}

const STATUS_OPTIONS = ["new", "reviewing", "interested", "dismissed"];

// One lead run: the stocks picked and their stars in the summary, the full report when expanded.
function runCard(r: Run, ratings: Record<string, { health: number }>, open: boolean): string {
  // Older runs recorded whether Claude was logged in to HotCopper; new requests don't ask for that.
  const hc = r.hotcopperLoggedIn === "yes" ? "HotCopper ✓" : r.hotcopperLoggedIn === "no" ? "HotCopper not logged in" : "";
  const picks = r.picks.map((p) => `<span class="pick"${stockAttr(p.code)}>${codePill(p.code)}${p.stars != null ? starsHtml(p.stars) : ""}${ratings[p.code] ? `<small class="muted">assessed ${ratings[p.code].health}★</small>` : ""}</span>`).join("");
  const rows = r.picks.map((p) => {
    const then = p.quick?.price ?? null;
    const since = then && p.priceNow ? p.priceNow / then - 1 : null;
    const sig = p.signals ?? {};
    const sigText = [
      sig.buzz ? `buzz ${sig.buzz}${sig.posts_30d != null ? ` (${sig.posts_30d} posts in 30 days)` : ""}` : "",
      sig.sentiment != null ? `sentiment ${Number(sig.sentiment) > 0 ? "+" : ""}${sig.sentiment}` : "",
      sig.catalyst ? `catalyst: ${sig.catalyst}${sig.catalyst_date ? ` (${sig.catalyst_date})` : ""}` : "",
      sig.insider_buying ? `insider buying: ${sig.insider_buying}` : "",
      sig.confidence ? `confidence ${sig.confidence}` : "",
    ].filter(Boolean).join(" · ");
    return `<li class="pick-row" data-lead="${p.leadId}">
      <div class="lead-head">${stockLink(p.code)} <span class="muted">${esc(p.name ?? "")}</span>${p.stars != null ? `<span title="Gold's quick check when found">${starsHtml(p.stars)}</span>` : ""}</div>
      <div class="chips"><span class="chip">${esc(label(p.strategy))}</span>${ratings[p.code] ? `<a class="chip" href="#/${esc(p.code)}/assess">Assessed ${ratings[p.code].health}★</a>` : ""}</div>
      <p>${esc(p.why).replace(/\n/g, "<br>")}</p>
      ${sigText ? `<p class="muted small">${esc(sigText)}</p>` : ""}
      <p class="small">Price when found ${price(then)}${p.priceNow != null ? ` · now ${price(p.priceNow)} <b class="${(since ?? 0) >= 0 ? "up" : "down"}">${pct(since, true)}</b>` : ""}${p.quick?.marketCap != null ? ` · market cap $${compact(p.quick.marketCap)}` : ""}${p.quick?.dailyValue != null ? ` · $${compact(p.quick.dailyValue)} a day` : ""}</p>
      ${p.sources.length ? `<p class="small">${p.sources.map((x, i) => `<a href="${esc(x.url)}" target="_blank" rel="noopener">${esc(x.title || `source ${i + 1}`)}</a>`).join(" · ")}</p>` : ""}
      <div class="lead-actions">
        <select aria-label="Status of ${esc(p.code)}">${STATUS_OPTIONS.map((x) => `<option value="${x}"${p.status === x ? " selected" : ""}>${x[0].toUpperCase() + x.slice(1)}</option>`).join("")}</select>
        <button class="ghost small" data-act="watch">☆ Watch</button>
        <a class="ghost small" href="${esc(askClaudeUrl(p.code))}" target="_blank" rel="noopener">Assess</a>
      </div></li>`;
  }).join("");
  return `<details class="card run"${open ? " open" : ""}>
    <summary>
      <div class="run-head"><b>${dateTime(r.ranAt)}</b><span class="muted small">${r.strategies.map(label).map(esc).join(", ") || "–"} · ${r.picks.length} picked${r.skipped.length ? `, ${r.skipped.length} skipped` : ""}${hc ? ` · ${hc}` : ""}</span></div>
      <div class="picks">${picks || `<span class="muted small">Nothing picked</span>`}</div>
    </summary>
    <div class="run-body">
      ${r.summary ? `<div class="md">${linkCodes(markdown(r.summary), [...r.picks.map((p) => p.code), ...r.skipped.map((x) => x.code)])}</div>` : ""}
      ${rows ? `<ul class="lead-list">${rows}</ul>` : ""}
      ${r.skipped.length ? `<h4>Skipped</h4><ul class="plain small">${r.skipped.map((x) => `<li>${stockLink(x.code)}: ${esc(x.reason)}</li>`).join("")}</ul>` : ""}
      ${r.sources.length ? `<h4>Sources checked</h4><ul class="plain small">${r.sources.map((x) => `<li>${x.reached ? "✓" : "✗"} ${esc(x.source)}${x.note ? ` <span class="muted">${esc(x.note)}</span>` : ""}</li>`).join("")}</ul>` : ""}
      <p class="muted small">${r.themes ? `Themes: ${esc(r.themes)} · ` : ""}${r.model ? `By ${esc(r.model)}` : ""}</p>
    </div>
  </details>`;
}
